<script setup lang="ts">
// The wizard HOST (`DataExportCreate.vue`, 701 ln — 525 of them rebuilt here and in the machine).
//
// v3 held 36 sibling `ref`s plus a template ref and drilled 20 props / 11 emits into each step.
// Here every step reads and writes `dataExportStore.draft` for itself, so the host owns only what
// no step can: which step is showing, and the two signals a step raises rather than stores.
//
// PRESENTATION (recorded decision). v3 is NOT a modal: `DataExport.vue:2-36` swaps the page body
// with `v-if="!createModal"` / `v-else`, and the five steps are stacked `CustomToggleArea`
// accordions whose numbered titles ("1. Source" … "5. Summary") lock through
// `:hide-chevron="!isStepAllowed(n)"`. The journey is preserved exactly — the page still swaps in
// place, an earlier step is still reachable by clicking its header — with 02's `Stepper` standing
// in for the hand-rolled accordion chrome and one step body rendered at a time.
import { computed, ref } from 'vue'

import { EXPORT_WIZARD_STEPS, useExportWizard } from '../../composables/useExportWizard'
import type { CreateExportJobOutcome } from '../../flows/CreateExportJobFlow'

import ExportLimitModal from './ExportLimitModal.vue'
import DataStep from './steps/DataStep.vue'
import DestinationStep from './steps/DestinationStep.vue'
import ScheduleStep from './steps/ScheduleStep.vue'
import SourceStep from './steps/SourceStep.vue'
import SummaryStep from './steps/SummaryStep.vue'

import { useI18n } from '@/shared/i18n'
import { Alert, Button, Stepper, useToast, type StepperStep, type StepperValue } from '@/shared/ui'

const emit = defineEmits<{ close: []; created: [] }>()

const { t } = useI18n()
const toast = useToast()
const wizard = useExportWizard()
const limitOpen = ref(false)

/** v3's accordion titles (`DataExportCreate.vue:5,49,88,110,126`), keyed. */
const STEP_LABEL_KEYS = [
  'dataExport.source.title',
  'dataExport.data.title',
  'dataExport.destination.title',
  'dataExport.schedule.title',
  'dataExport.summary.title',
] as const

const steps = computed<StepperStep[]>(() =>
  EXPORT_WIZARD_STEPS.map((value, index) => ({
    value,
    label: t(STEP_LABEL_KEYS[index] ?? ''),
    // v3's `:hide-chevron="!isStepAllowed(n)"` — an unreachable step stays visible but inert.
    disabled: !wizard.isStepAllowed(value),
  })),
)
const isLastStep = computed(() => wizard.step.value === EXPORT_WIZARD_STEPS.length)

function onStep(value: StepperValue): void {
  wizard.goTo(Number(value))
}

/**
 * v3's footer submit was labelled `"Next"` and gated on `!isStepAllowed(4)` (`:149`); the outcome
 * routing is the fix. `limitReached` is the upsell modal and NEVER a toast (contract §12), while a
 * genuine failure keeps the draft so the user can retry (`CreateExportJobFlow:228`).
 */
async function submit(): Promise<void> {
  const outcome = await wizard.complete()
  if (outcome.status === 'created') {
    toast.success(t('dataExport.wizard.created'))
    emit('created')
    close()
    return
  }
  if (outcome.status === 'limitReached') {
    limitOpen.value = true
    return
  }
  toast.error(rejectionMessage(outcome))
}

/**
 * The backend's own reason for a rejected create, when it named one (ORP-20385). Before this the
 * wizard toasted one static key for every failure, so a user whose body was rejected for a NAMED
 * reason — a duplicate export name, a field the schema guard refused — was told only to "try
 * again" and retried the identical body.
 *
 * SCOPE IS DELIBERATE, and it is the wire that sets it. `/export-service/create-export-job/`
 * rejects with a message on exactly two classes: the duplicate-name `400 {success:false, message}`
 * and the payload-schema `400 {code:'errors.validationError', message}`. Both classify as
 * `validation`; `conflict` is here for the reserved `EXPORT_NAME_TAKEN` (409) that supersedes the
 * first. Everything else — transport, timeout, 5xx — has no backend message at all, and
 * `ApiError.detail` falls back to axios's own "Request failed with status code N" there, which is
 * noise in a toast rather than an explanation. The static key stays right for those.
 *
 * NOTE the endpoint also answers some failures `200 {success:false, message}` (`views.py:449-451`),
 * which never reach this branch because they are not errors on the wire. Out of scope here.
 *
 * `userVisible` is the backend's display-policy signal (contract 03 §5) and still overrides: a
 * body marked private falls back to the generic key even when it carries a detail.
 */
function rejectionMessage(outcome: CreateExportJobOutcome): string {
  const named =
    outcome.status === 'failed' &&
    (outcome.error.kind === 'validation' || outcome.error.kind === 'conflict') &&
    outcome.error.userVisible
  const detail = named ? outcome.error.detail.trim() : ''
  return detail
    ? t('dataExport.wizard.createFailedDetail', { detail })
    : t('dataExport.wizard.createFailed')
}

function close(): void {
  wizard.reset()
  emit('close')
}
</script>

<template>
  <section
    class="export-wizard grid gap-lg min-w-0 font-sans"
    :aria-label="t('dataExport.wizard.title')"
  >
    <Stepper
      v-bind="{ ariaLabel: t('dataExport.wizard.stepsLabel') }"
      :model-value="wizard.step.value"
      :steps="steps"
      @update:model-value="onStep"
    />

    <!-- FIXED: v3 raised both of these from a watcher that ran AFTER the step had advanced
         (`DataExportCreate.vue:682-698`), so the user saw the warning *and* landed on an empty
         Step 2. They now block the advance and the message is rendered in place. -->
    <Alert v-if="wizard.blockedKey.value" tone="warning" data-testid="wizard-blocked" role="alert">
      {{ t(wizard.blockedKey.value) }}
    </Alert>

    <div class="export-wizard__body min-w-0">
      <SourceStep v-if="wizard.step.value === 1" />
      <DataStep
        v-else-if="wizard.step.value === 2"
        :available-events="wizard.availableEvents.value"
      />
      <DestinationStep
        v-else-if="wizard.step.value === 3"
        @update:valid="wizard.destinationValid.value = $event"
      />
      <ScheduleStep
        v-else-if="wizard.step.value === 4"
        v-model="wizard.schedule.value"
        :allowed-frequencies="wizard.allowedFrequencies.value"
      />
      <SummaryStep v-else :summary="wizard.summary.value" />
    </div>

    <footer class="export-wizard__footer flex justify-end gap-sm pt-md border-t border-t-border">
      <Button variant="outline" @click="close">{{ t('common.cancel') }}</Button>
      <Button v-if="wizard.step.value > 1" variant="soft" @click="wizard.back">
        {{ t('dataExport.wizard.back') }}
      </Button>
      <Button
        v-if="!isLastStep"
        :disabled="wizard.nextDisabled(wizard.step.value)"
        @click="wizard.next"
      >
        {{ t('dataExport.wizard.next') }}
      </Button>
      <Button
        v-else
        :loading="wizard.submitting.value"
        :disabled="!wizard.isStepAllowed(EXPORT_WIZARD_STEPS.length)"
        @click="submit"
      >
        {{ t('dataExport.wizard.submit') }}
      </Button>
    </footer>

    <ExportLimitModal v-model="limitOpen" />
  </section>
</template>
